import React, { useEffect, useRef, useState } from 'react'
import o from '../../components/Overlay.module.css'
import d from './Overlays.module.css'
import { apiJson } from '../../lib/api'
import { useLayer } from '../../components/layers'
import { PendingButton, inertClass, useSlow, Failed } from '../../components/pending'
import { dmy, nowHm, type ClockOffPreview } from './model'

// Clock all off (§7; V4 clockAllOff): a confirm, never a one-click action.
// The finish time is one shared time or each worker's own last scan; the
// server previews exactly what it will write, the confirm states that count,
// and the batch comes back as one Undo (routes/timesheets.js).
//
// V4's fixed "16:30" and "17:00" chips stand in for "the standard shift
// ends" (§7), which is not a setting anywhere yet, so they are not offered:
// any time can still be typed (DASH-8).

type Mode = 'now' | 'time' | 'last_scan'

export interface ClockOffDone {
  batch_id: string
  flash: string
  note?: string
}

interface Props {
  user: { getIdToken: () => Promise<string> } | null
  today: string
  live: number
  onClose: () => void
  onDone: (r: ClockOffDone) => void
}

export function ClockOffDialog({ user, today, live, onClose, onDone }: Props) {
  const [mode, setMode] = useState<Mode>('now')
  const [time, setTime] = useState(() => nowHm())
  const [preview, setPreview] = useState<ClockOffPreview | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const seq = useRef(0)
  const userRef = useRef(user)
  userRef.current = user

  const body = mode === 'last_scan' ? { today, mode: 'last_scan' } : { today, mode: 'time', time }
  const bodyKey = JSON.stringify(body)

  useEffect(() => {
    const my = ++seq.current
    setError(null)
    if (mode !== 'last_scan' && !/^\d{2}:\d{2}$/.test(time)) {
      setPreview(null)
      return
    }
    apiJson<ClockOffPreview>(userRef.current, '/api/timesheets/clock-off/preview', { body })
      .then((p) => {
        if (my === seq.current) setPreview(p)
      })
      .catch((e) => {
        if (my !== seq.current) return
        setPreview(null)
        setError(e?.message || 'Could not work out who would be clocked off')
      })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bodyKey])

  const confirm = async () => {
    setBusy(true)
    setError(null)
    try {
      const r = await apiJson<ClockOffDone>(userRef.current, '/api/timesheets/clock-off', { body })
      onDone(r)
    } catch (e: any) {
      setError(e?.message || 'Could not clock off')
      setBusy(false)
    }
  }

  // A layer (h 425-458): Escape and the scrim close it. It is a question, not
  // an edit, so it closes without asking; nothing closes while it is writing.
  const panelEl = useRef<HTMLDivElement>(null)
  const { requestClose } = useLayer(true, onClose, { el: panelEl, modal: true, busy })
  // While it writes (h 3984-3992) the choices take no presses, and dim with
  // the button's "Clocking off…" once the wait is real.
  const inert = inertClass(busy, useSlow(busy))

  const n = preview?.count ?? 0
  const workers = (k: number) => `${k} worker${k === 1 ? '' : 's'}`
  return (
    <div className={o.scrim} onClick={requestClose}>
      <div ref={panelEl} tabIndex={-1} className={`${o.modal} ${d.clockModal}`} role="dialog" aria-label="Clock all off" onClick={(e) => e.stopPropagation()}>
        <h2 className={o.title}>Clock all off</h2>
        <p className={d.clockSub}>Ends the shift for all {workers(live)} still clocked in. Pick the finish time to record against every one of them.</p>
        <div className={d.clockField}>
          <span className={o.label}>Clock off at</span>
          <label className={`${mode === 'last_scan' ? d.timeFieldOff : d.timeField} ${inert}`}>
            <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.75" strokeLinecap="round" aria-hidden="true">
              <circle cx="12" cy="12" r="9" />
              <path d="M12 7v5l3.5 2" />
            </svg>
            <input
              type="time"
              aria-label="Finish time"
              value={time}
              disabled={mode === 'last_scan'}
              onChange={(e) => {
                setTime(e.target.value)
                setMode('time')
              }}
            />
            <span>{mode === 'last_scan' ? "Each worker's last scan" : dmy(today)}</span>
          </label>
        </div>
        <div className={`${d.presetRow} ${inert}`}>
          <button
            type="button"
            className={mode === 'now' ? d.chipOn : d.chip}
            aria-pressed={mode === 'now'}
            onClick={() => {
              setTime(nowHm())
              setMode('now')
            }}
          >
            Now ({mode === 'now' ? time : nowHm()})
          </button>
          <button type="button" className={mode === 'last_scan' ? d.chipOn : d.chip} aria-pressed={mode === 'last_scan'} onClick={() => setMode('last_scan')}>
            Last scan per worker
          </button>
        </div>
        <div className={`${o.warn} ${d.clockWarn}`}>
          <WarnIcon />
          <span>Each affected timesheet is marked as adjusted by you, so payroll can see it was a bulk clock-off rather than a worker scan.</span>
        </div>
        {preview && preview.blocked.length > 0 && (
          <div className={`${o.warn} ${d.clockWarnTight}`} data-testid="clockoff-blocked">
            <WarnIcon />
            <span>
              {workers(preview.blocked.length)} will stay clocked in:
              <ul className={d.blockList}>
                {preview.blocked.map((b) => (
                  <li key={b.shift_id}>
                    {b.worker_name}: {b.why}
                  </li>
                ))}
              </ul>
            </span>
          </div>
        )}
        {preview && preview.earlier_open > 0 && (
          <p className={d.clockNote}>
            {preview.earlier_open} earlier shift{preview.earlier_open === 1 ? ' has' : 's have'} no clock-off. They are not touched here; fix them in Timesheets.
          </p>
        )}
        <div className={o.foot}>
          <button type="button" className={o.ghost} disabled={busy} onClick={requestClose}>
            Cancel
          </button>
          <PendingButton className={o.primary} busy={busy} pending="Clocking off…" disabled={!preview || n === 0} onClick={confirm}>
            {`Clock off ${workers(n)}`}
          </PendingButton>
        </div>
        <Failed text={error} />
      </div>
    </div>
  )
}

function WarnIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M10.3 4 2.5 17.5a2 2 0 0 0 1.7 3h15.6a2 2 0 0 0 1.7-3L13.7 4a2 2 0 0 0-3.4 0Z" />
      <path d="M12 9v4M12 17h.01" />
    </svg>
  )
}
